import * as React from 'react'
import { NumberField as BaseNumberField } from '@base-ui/react/number-field'
import IconButton from '@mui/material/IconButton'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

/**
 * This component is a placeholder for FormControl to correctly set the shrink label state on SSR.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SSRInitialFilled(_: BaseNumberField.Root.Props) {
    return null
}
SSRInitialFilled.muiName = 'Input'

export default function NumberField({
    id: idProp,
    label,
    error,
    helperText,
    size = 'medium',
    endAdornment,
    sx,
    ...other
}: BaseNumberField.Root.Props & {
    label?: React.ReactNode
    size?: 'small' | 'medium'
    error?: boolean
    helperText?: React.ReactNode
    endAdornment?: React.ReactNode
    sx?: any
}) {
    let id = React.useId()
    if (idProp) {
        id = idProp
    }
    return (
        <BaseNumberField.Root
            {...other}
            render={(props, state) => (
                <FormControl size={size} ref={props.ref} disabled={state.disabled} required={state.required} error={error} variant="outlined" sx={sx}>
                    {props.children}
                </FormControl>
            )}
        >
            <SSRInitialFilled {...other} />
            <InputLabel htmlFor={id}>{label}</InputLabel>
            <BaseNumberField.Input
                id={id}
                render={(props, state) => (
                    <OutlinedInput
                        label={label}
                        inputRef={props.ref}
                        value={state.inputValue}
                        onBlur={props.onBlur}
                        onChange={props.onChange}
                        onKeyUp={props.onKeyUp}
                        onKeyDown={props.onKeyDown}
                        onFocus={(e: any) => {
                            // Markiere automatisch den gesamten Text beim Fokussieren
                            e.target.select()
                            if (props.onFocus) {
                                props.onFocus(e)
                            }
                        }}
                        onInput={(e: any) => {
                            // Begrenze auf maximal 2 Nachkommastellen
                            const input = e.target as HTMLInputElement
                            const value = input.value
                            
                            if (value.includes('.') || value.includes(',')) {
                                const parts = value.split(/[.,]/)
                                if (parts.length === 2 && parts[1].length > 2) {
                                    // Kürze auf 2 Nachkommastellen
                                    const newValue = parts[0] + '.' + parts[1].substring(0, 2)
                                    input.value = newValue
                                    // Trigger onChange manually
                                    const changeEvent = new Event('change', { bubbles: true })
                                    input.dispatchEvent(changeEvent)
                                }
                            }
                        }}
                        slotProps={{
                            input: props,
                        }}
                        endAdornment={
                            <>
                                {endAdornment && (
                                    <InputAdornment position="end" sx={{ mr: 0, ml: 0 }}>
                                        {endAdornment}
                                    </InputAdornment>
                                )}
                                <InputAdornment
                                    position="end"
                                    sx={{
                                        flexDirection: 'column',
                                        maxHeight: 'unset',
                                        alignSelf: 'stretch',
                                        borderLeft: '1px solid',
                                        borderColor: 'divider',
                                        ml: 0,
                                        '& button': {
                                            py: 0,
                                            flex: 1,
                                            borderRadius: 0.5,
                                        },
                                    }}
                                >
                                    <BaseNumberField.Increment render={<IconButton size={size} aria-label="Increase" />}>
                                        <KeyboardArrowUpIcon fontSize={size} sx={{ transform: 'translateY(2px)' }} />
                                    </BaseNumberField.Increment>

                                    <BaseNumberField.Decrement render={<IconButton size={size} aria-label="Decrease" />}>
                                        <KeyboardArrowDownIcon fontSize={size} sx={{ transform: 'translateY(-2px)' }} />
                                    </BaseNumberField.Decrement>
                                </InputAdornment>
                            </>
                        }
                        sx={{ pr: 0 }}
                    />
                )}
            />
            {helperText && <FormHelperText sx={{m: 0}}>{helperText}</FormHelperText>}
        </BaseNumberField.Root>
    )
}
